/**
 * `useRedirectToBestMatch` is a custom hook designed to redirect the user
 * to the most suitable variant of a product, based on certain selected attributes.
 *
 * Flow:
 * 1. Access the current product details from the `useProductDetailsStore`.
 * 2. Use the `getBestMatch` function to identify the best matching variant.
 *    - This is determined based on the attributes passed and the product variants available.
 *    - The function prioritizes variants that match the provided key and sorts them by price, selecting the cheapest option.
 * 3. The primary function, `redirectToBestMatch`, takes care of triggering the redirect.
 *    - If the best match variant is found and has a valid URL, it will redirect the user to that product variant.
 *
 * Usage:
 * This hook is primarily beneficial in scenarios where, based on user selection, we want to show
 * the most relevant or cost-effective variant of a product.
 */

import { useRouter } from 'next/router';
import { useProductDetailsStore } from '~/features/product-details-page/hooks/useProductDetailsStore';

const useRedirectToBestMatch = () => {
    const router = useRouter();
    const { product } = useProductDetailsStore();
    // const { translate } = useTranslation();

    /**
     * Identifies the best matching variant of a product based on selected attributes.
     *
     * @param {Array} variants - List of available product variants.
     * @param {Object} selectedAttributes - The attributes to match against.
     * @param {string} [key] - The specific attribute key to consider in the matching process.
     * @returns {Object|null} The best match variant or null if no match is found.
     */
    const getBestMatch = (
        variants: any[] = [],
        selectedAttributes: any,
        key?: string
    ): any | null => {
        if (!key || !variants.length || !selectedAttributes) {
            return null;
        }

        const matchingVariants = variants.filter((v) => {
            return v.variationValues[key] === selectedAttributes[key];
        });

        const sortedByPrice = matchingVariants.sort((a, b) => a.price - b.price);
        return { ...sortedByPrice[0] };
    };

    /**
     * Triggers a redirect to the best match variant of the current product based on selected attributes.
     *
     * @param {Object} selectedAttributes - The attributes to match against.
     */
    const redirectToBestMatch = (selectedAttributes: any, id: string) => {
        const bestMatch = getBestMatch(product?.c_variants, selectedAttributes, id);
        if (bestMatch?.c_pageURL) {
            return router.replace(bestMatch?.c_pageURL, undefined, {
                shallow: true,
            });
        }
    };

    return { redirectToBestMatch };
};

export default useRedirectToBestMatch;
